import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    Alert,
    Chip,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Application } from '@microsoft/microsoft-graph-types';
import { v4 as uuidv4 } from 'uuid'; // For generating scope IDs
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import { Client } from '@microsoft/microsoft-graph-client';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SecurityIcon from '@mui/icons-material/Security';

export interface PermissionScope {
    id: string; // Make required
    adminConsentDescription: string;
    adminConsentDisplayName: string;
    isEnabled: boolean;
    type: string;
    value: string;
    userConsentDescription: string;
    userConsentDisplayName: string;
    origin?: string;
}

interface ExposeApiProps {
    app: Application; // Original app for reference
    modifiedApp: Application;
    setModifiedApp: React.Dispatch<React.SetStateAction<Application>>;
    updateApplication: () => Promise<void>; // Callback to save changes
    graphAccessToken?: string; 
}

interface PreAuthorizedApplication {
  appId: string | null;
  displayName?: string | null;
  delegatedPermissionIds: string[];
}

const mapGraphScopeToPermissionScope = (scope: any): PermissionScope => {
    return {
        id: scope.id || uuidv4(), // Ensure ID always exists
        adminConsentDescription: scope.adminConsentDescription || '',
        adminConsentDisplayName: scope.adminConsentDisplayName || '',
        isEnabled: scope.isEnabled === undefined ? true : scope.isEnabled,
        type: scope.type || 'User',
        value: scope.value || '',
        userConsentDescription: scope.userConsentDescription || '',
        userConsentDisplayName: scope.userConsentDisplayName || '',
        origin: scope.origin
    };
};

// Default scope template
const defaultScope: Omit<PermissionScope, 'id'> = {
    adminConsentDescription: '',
    adminConsentDisplayName: '',
    isEnabled: true,
    type: 'User', // Default to User consentable scopes
    userConsentDescription: '',
    userConsentDisplayName: '',
    value: '', // Scope name like 'Files.Read'
};

const ExposeApi: React.FC<ExposeApiProps> = ({
    app,
    modifiedApp,
    setModifiedApp,
    updateApplication,
    graphAccessToken
}) => {
    // State for managing Application ID URI
    // 1. First, modify the state initialization to respect the app's current state
    const [appIdUri, setAppIdUri] = useState<string>(
        modifiedApp.identifierUris?.[0] || ""  // Change from default to empty string
    );
    // 2. Add state to track if the user wants to expose an API
    const [exposeApiEnabled, setExposeApiEnabled] = useState<boolean>(
        !!(modifiedApp.identifierUris && modifiedApp.identifierUris.length > 0)
    );

    // State for Add/Edit Scope Dialog
    const [scopeDialogOpen, setScopeDialogOpen] = useState<boolean>(false);
    const [currentScope, setCurrentScope] = useState<PermissionScope | Omit<PermissionScope, 'id'>>(defaultScope); 
    const [editingScopeIndex, setEditingScopeIndex] = useState<number | null>(null); // null for adding, index for editing
    const [scopeErrors, setScopeErrors] = useState<Record<string, string>>({});

    // State for Delete Confirmation Dialog
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [scopeToDeleteIndex, setScopeToDeleteIndex] = useState<number | null>(null);

    const [apiError, setApiError] = useState<string | null>(null);

    const [preAuthorizedAppId, setPreAuthorizedAppId] = useState<string>("");
    const [preAuthorizedAppName, setPreAuthorizedAppName] = useState<string>("");
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
    const [preAuthorizedAppDialogOpen, setPreAuthorizedAppDialogOpen] = useState<boolean>(false);
      const [preAuthorizedApps, setPreAuthorizedApps] = useState<PreAuthorizedApplication[]>(
        (modifiedApp.api?.preAuthorizedApplications || []).map(app => ({
          appId: app.appId || null,
          displayName: null, // displayName is not available on the Graph PreAuthorizedApplication object
          delegatedPermissionIds: app.delegatedPermissionIds || []
        }))
      );
    const [preAuthErrorMessage, setPreAuthErrorMessage] = useState<string>("");
    const [isValidatingAppId, setIsValidatingAppId] = useState<boolean>(false);
    const [validatedAppInfo, setValidatedAppInfo] = useState<{appId: string, displayName: string} | null>(null);
    const [hasPreAuthChanges, setHasPreAuthChanges] = useState(false);

    const [isEditingPreAuthorizedApp, setIsEditingPreAuthorizedApp] = useState<boolean>(false);
    const [editingPreAuthorizedAppIndex, setEditingPreAuthorizedAppIndex] = useState<number | null>(null);

    const [appDisplayNameCache, setAppDisplayNameCache] = useState<Record<string, string>>({});

    const [hasToggleChange, setHasToggleChange] = useState<boolean>(false);
    const [saveAttemptCount, setSaveAttemptCount] = useState<number>(0);
    const [showSecondClickGuidance, setShowSecondClickGuidance] = useState<boolean>(false);

    // Add a utility function for consistent transformation
    const transformPreAuthAppForGraphApi = (preAuthApp: PreAuthorizedApplication) => {
        return {
            appId: preAuthApp.appId,
            delegatedPermissionIds: preAuthApp.delegatedPermissionIds
        };
    };
    
    // State for managing scopes
    const [scopes, setScopes] = useState<PermissionScope[]>(
        (modifiedApp.api?.oauth2PermissionScopes || []).map(mapGraphScopeToPermissionScope)
    );

    // Update local state if modifiedApp changes externally (e.g., after save/revert)
    useEffect(() => {
        setAppIdUri(modifiedApp.identifierUris?.[0] || `api://${modifiedApp.appId}`);
        setScopes((modifiedApp.api?.oauth2PermissionScopes || []).map(mapGraphScopeToPermissionScope));
    }, [modifiedApp]);

    useEffect(() => {
        setPreAuthorizedApps((modifiedApp.api?.preAuthorizedApplications || []).map(app => ({
          appId: app.appId || null,
          displayName: null, // displayName is not available on the Graph PreAuthorizedApplication object
          delegatedPermissionIds: app.delegatedPermissionIds || []
        })));
        }, [modifiedApp.api?.preAuthorizedApplications]);

    // --- Application ID URI ---
    const handleAppIdUriChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newUri = event.target.value;
        setAppIdUri(newUri);
        setModifiedApp(prev => ({
            ...prev,
            identifierUris: [newUri] // Graph expects an array
        }));
    };

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            // Consider adding a temporary success message/snackbar
            console.log("Copied App ID URI to clipboard");
        }).catch(err => {
            console.error("Failed to copy App ID URI:", err);
        });
    };

    // --- Scope Dialog ---
    const validateScope = (scope: PermissionScope | Omit<PermissionScope, 'id'>): boolean => {
        const errors: Record<string, string> = {};
        if (!scope.adminConsentDisplayName?.trim()) errors.adminConsentDisplayName = 'Admin consent display name is required.';
        if (!scope.adminConsentDescription?.trim()) errors.adminConsentDescription = 'Admin consent description is required.';
        if (!scope.value?.trim()) errors.value = 'Scope value (name) is required.';
        if (scope.value?.includes(' ')) errors.value = 'Scope value cannot contain spaces.';
        if (!['User', 'Admin'].includes(scope.type)) errors.type = 'Invalid scope type. Must be "User" or "Admin".';
        // Add more validation as needed (e.g., uniqueness of 'value')
        setScopeErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleOpenAddScopeDialog = () => {
        setCurrentScope({ ...defaultScope, id: uuidv4(), isEnabled: true }); // Generate ID for new scope
        setEditingScopeIndex(null);
        setScopeErrors({});
        setScopeDialogOpen(true);
    };

    const handleOpenEditScopeDialog = (index: number) => {
        setCurrentScope({ ...scopes[index] }); // Edit a copy
        setEditingScopeIndex(index);
        setScopeErrors({});
        setScopeDialogOpen(true);
    };

    const handleScopeDialogClose = () => {
        setScopeDialogOpen(false);
        setCurrentScope(defaultScope);
        setEditingScopeIndex(null);
        setScopeErrors({});
    };

    const handleScopeChange = (field: keyof PermissionScope, value: string | boolean) => { // Changed OAuth2PermissionScope to PermissionScope
        // Explicitly type 'prev' and handle potential symbol keys safely
        setCurrentScope((prev: PermissionScope | Omit<PermissionScope, 'id'>) => ({
             ...prev,
             [field as string]: value // Cast field to string for indexing
        }));
        // Clear specific error when user types
        const fieldStr = field as string; // Cast field to string for indexing
        if (scopeErrors[fieldStr]) {
            setScopeErrors((prev: Record<string, string>) => { // Explicitly type 'prev'
                const newErrors = { ...prev };
                delete newErrors[fieldStr]; // Use string index
                return newErrors;
            });
        }
    };

    const handleSaveScope = () => {
        if (!validateScope(currentScope)) return;
    
        const updatedScopes = [...scopes];
        if (editingScopeIndex !== null) {
            // Editing existing scope
            updatedScopes[editingScopeIndex] = currentScope as PermissionScope;
        } else {
            // Adding new scope (ensure it has an ID)
            const newScope = { 
                ...currentScope, 
                id: (currentScope as PermissionScope).id || uuidv4() 
            } as PermissionScope;
            updatedScopes.push(newScope);
        }
    
        setScopes(updatedScopes);
        
        // Azure Best Practice: Format scope data properly for Graph API
        setModifiedApp(prev => {
            // Create or update the api property
            const updatedApi = {
                ...(prev.api || {}),
                // Ensure all required fields are present for Graph API
                oauth2PermissionScopes: updatedScopes.map(scope => ({
                    id: scope.id,
                    adminConsentDescription: scope.adminConsentDescription,
                    adminConsentDisplayName: scope.adminConsentDisplayName,
                    isEnabled: scope.isEnabled,
                    type: scope.type,
                    value: scope.value,
                    userConsentDescription: scope.userConsentDescription || scope.adminConsentDescription, // Fall back to admin description
                    userConsentDisplayName: scope.userConsentDisplayName || scope.adminConsentDisplayName, // Fall back to admin display name
                    origin: scope.origin
                }))
            };
            
            return {
                ...prev,
                api: updatedApi
            };
        });
        
        handleScopeDialogClose();
    };

    // --- Delete Scope ---
    const handleOpenDeleteConfirm = (index: number) => {
        setScopeToDeleteIndex(index);
        setDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setScopeToDeleteIndex(null);
        setDeleteConfirmOpen(false);
    };

    // Azure Best Practice: Two-step scope deletion process
    const handleDeleteScopeConfirm = () => {
        if (scopeToDeleteIndex !== null) {
            const scopeToDelete = scopes[scopeToDeleteIndex];
            
            // Check if the scope is already disabled
            if (scopeToDelete.isEnabled) {
            setApiError(`Permission '${scopeToDelete.value}' must be disabled before deletion. Please edit the scope to disable it first.`);
            handleCloseDeleteConfirm();
            return;
            }
            
            // Store the deleted scope ID to check references
            const deletedScopeId = scopeToDelete.id;
            
            // If already disabled, proceed with deletion
            const updatedScopes = scopes.filter((_, index) => index !== scopeToDeleteIndex);
            setScopes(updatedScopes);
            
            setModifiedApp(prev => ({
            ...prev,
            // Update scopes within the 'api' property
            api: {
                ...prev.api,
                oauth2PermissionScopes: updatedScopes
            }
            }));
            
            // Clean up any references to the deleted scope in pre-authorized applications
            setTimeout(() => {
            syncPreAuthorizedAppsWithValidScopes();
            }, 0);
        }
        
        handleCloseDeleteConfirm();
    };

    // Add this function to open pre-authorized app dialog with validation
    const handleOpenPreAuthorizedAppDialog = () => {
        // First check and clean up any invalid scope references
        syncPreAuthorizedAppsWithValidScopes();
        
        // Then open the dialog
        setPreAuthorizedAppDialogOpen(true);
    };

    // Add this helper function to your component
    const handleToggleScopeEnabled = (index: number, enabled: boolean) => {
        const updatedScopes = [...scopes];
        updatedScopes[index] = {
        ...updatedScopes[index],
        isEnabled: enabled
        };
        
        setScopes(updatedScopes);
        setModifiedApp(prev => ({
        ...prev,
        api: {
            ...prev.api,
            oauth2PermissionScopes: updatedScopes
        }
        }));
    };

    // Add this function to validate an App ID
    const validateAppId = async (appId: string) => {
    if (!appId.trim()) {
        setPreAuthErrorMessage("App ID is required");
        return;
    }
    
    // Basic format validation (GUID format)
    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidPattern.test(appId)) {
        setPreAuthErrorMessage("App ID must be a valid GUID format");
        return;
    }

    // Add the Graph access token check here, before setting isValidatingAppId
    if (!graphAccessToken) {
        setPreAuthErrorMessage("Microsoft Graph access token not available");
        setIsValidatingAppId(false);
        return;
    }
    
    setIsValidatingAppId(true);
    setPreAuthErrorMessage("");
    
    try {
        const client = Client.init({
        authProvider: (done) => {
            if (!graphAccessToken) {
            done(new Error("No access token available"), null);
            } else {
            done(null, graphAccessToken);
            }
        }
        });
        
        // Look up the application by ID to verify it exists and get its display name
        const result = await client
        .api(`/applications`)
        .filter(`appId eq '${appId}'`)
        .select('displayName,appId')
        .get();
        
        if (result.value && result.value.length > 0) {
        const appInfo = result.value[0];
        setPreAuthorizedAppName(appInfo.displayName);
        setValidatedAppInfo({
            appId: appInfo.appId,
            displayName: appInfo.displayName
        });
        setPreAuthErrorMessage("");
        } else {
        setPreAuthErrorMessage("No application found with this App ID");
        setValidatedAppInfo(null);
        }
    } catch (err: any) {
        console.error("Error validating App ID:", err);
        setPreAuthErrorMessage(`Error validating App ID: ${err.message}`);
        setValidatedAppInfo(null);
    } finally {
        setIsValidatingAppId(false);
    }
    };

    // Reset function
    const resetPreAuthorizedAppDialog = () => {
    setPreAuthorizedAppDialogOpen(false);
    setPreAuthorizedAppId("");
    setPreAuthorizedAppName("");
    setSelectedPermissionIds([]);
    setPreAuthErrorMessage("");
    setValidatedAppInfo(null);
    setIsEditingPreAuthorizedApp(false);
    setEditingPreAuthorizedAppIndex(null);
    };

    // Edit handler
    const handleEditPreAuthorizedApp = (index: number) => {
    const appToEdit = preAuthorizedApps[index];
    
    setIsEditingPreAuthorizedApp(true);
    setEditingPreAuthorizedAppIndex(index);
    
    setPreAuthorizedAppId(appToEdit.appId || "");
    setSelectedPermissionIds(appToEdit.delegatedPermissionIds || []);
    setPreAuthorizedAppName(appToEdit.displayName || "");
    
    setValidatedAppInfo({
        appId: appToEdit.appId || "",
        displayName: appToEdit.displayName || appToEdit.appId || ""
    });
    
    setPreAuthorizedAppDialogOpen(true);
    };

    // Function to handle adding/editing a pre-authorized application
    const handleAddOrUpdatePreAuthorizedApp = () => {
    if (!validatedAppInfo || !validatedAppInfo.appId) {
        setPreAuthErrorMessage("Please validate the client App ID first");
        return;
    }
    
    if (selectedPermissionIds.length === 0) {
        setPreAuthErrorMessage("Please select at least one permission to grant");
        return;
    }
    
    // Get the existing display name or use the one from validation
    const displayName = isEditingPreAuthorizedApp ? 
        (preAuthorizedApps[editingPreAuthorizedAppIndex!].displayName || validatedAppInfo.displayName) : 
        validatedAppInfo.displayName;
    
    const preAuthorizedApp = {
        appId: validatedAppInfo.appId,
        displayName: displayName,
        delegatedPermissionIds: selectedPermissionIds
    };

    // Update the cache
    setAppDisplayNameCache(prev => ({
        ...prev,
        [validatedAppInfo.appId]: displayName
    }));

    let updatedPreAuthorizedApps: PreAuthorizedApplication[];
    
    if (isEditingPreAuthorizedApp && editingPreAuthorizedAppIndex !== null) {
        updatedPreAuthorizedApps = [...preAuthorizedApps];
        updatedPreAuthorizedApps[editingPreAuthorizedAppIndex] = preAuthorizedApp;
    } else {
        if (preAuthorizedApps.some(app => app.appId === validatedAppInfo.appId)) {
        setPreAuthErrorMessage("This application is already pre-authorized");
        return;
        }
        updatedPreAuthorizedApps = [...preAuthorizedApps, preAuthorizedApp];
    }
    
    // Use preserveDisplayNames to ensure all display names are maintained
    updatedPreAuthorizedApps = preserveDisplayNames(updatedPreAuthorizedApps);
    
    setPreAuthorizedApps(updatedPreAuthorizedApps);
    
    setModifiedApp(prev => ({
        ...prev,
        api: {
        ...(prev.api || {}),
        preAuthorizedApplications: updatedPreAuthorizedApps.map(transformPreAuthAppForGraphApi)
        }
    }));
    
    setHasPreAuthChanges(true);
    resetPreAuthorizedAppDialog();
    };

    // Add this function to handle removing a pre-authorized application
    const handleRemovePreAuthorizedApp = (appIdToRemove: string) => {
    const updatedPreAuthorizedApps = preAuthorizedApps.filter(app => app.appId !== appIdToRemove);
    
    // Use preserveDisplayNames to ensure all display names are maintained
    const preservedApps = preserveDisplayNames(updatedPreAuthorizedApps);
    
    setPreAuthorizedApps(preservedApps);
    
    // Update modifiedApp with the updated list
    setModifiedApp(prev => ({
        ...prev,
        api: {
        ...(prev.api || {}),
        preAuthorizedApplications: preservedApps.map(transformPreAuthAppForGraphApi)
        }
    }));
    setHasPreAuthChanges(true);
    };

    // A function to fetch display names for all pre-authorized apps
    const fetchAppDisplayNames = async () => {
    if (!graphAccessToken || !preAuthorizedApps.length) return;
    
    const appIdsToFetch = preAuthorizedApps
        .filter(app => app.appId && !appDisplayNameCache[app.appId])
        .map(app => app.appId as string);
    
    if (appIdsToFetch.length === 0) return;
    
    try {
        const client = Client.init({
        authProvider: (done) => {
            if (!graphAccessToken) {
            done(new Error("No access token available"), null);
            } else {
            done(null, graphAccessToken);
            }
        }
        });
        
        // Create a batch request to avoid multiple API calls
        const batchRequests = appIdsToFetch.map((appId, index) => ({
        id: index.toString(),
        method: "GET",
        url: `/applications?$filter=appId eq '${appId}'&$select=displayName,appId`
        }));
        
        const batchResponse = await client.api('/$batch').post({
        requests: batchRequests
        });
        
        const newCache = {...appDisplayNameCache};
        batchResponse.responses.forEach((response: any) => {
        if (response.status === 200 && response.body.value && response.body.value.length > 0) {
            const appInfo = response.body.value[0];
            if (appInfo.appId && appInfo.displayName) {
            newCache[appInfo.appId] = appInfo.displayName;
            }
        }
        });
        
        setAppDisplayNameCache(newCache);
    } catch (err: any) {
        console.error("Error fetching app display names:", err);
    }
    };

    // Use useEffect to fetch display names when component loads or preAuthorizedApps changes
    useEffect(() => {
    fetchAppDisplayNames();
    }, [preAuthorizedApps, graphAccessToken, fetchAppDisplayNames]);

    // Update how preAuthorizedApps is initialized to use the cache if available
    useEffect(() => {
    if (!modifiedApp.api?.preAuthorizedApplications) return;
    
    const initialApps = modifiedApp.api.preAuthorizedApplications.map(app => ({
        appId: app.appId || null,
        displayName: app.appId ? appDisplayNameCache[app.appId] || null : null,
        delegatedPermissionIds: app.delegatedPermissionIds || []
    }));
    
    // Use preserveDisplayNames to maintain display names from previous state
    const preservedApps = preserveDisplayNames(initialApps);
    setPreAuthorizedApps(preservedApps);
    }, [modifiedApp.api?.preAuthorizedApplications, appDisplayNameCache]);

    // Add this function to handle permission selection changes
    const handlePermissionSelectionChange = (event: React.ChangeEvent<HTMLInputElement>, scopeId: string) => {
    if (event.target.checked) {
        setSelectedPermissionIds(prev => [...prev, scopeId]);
    } else {
        setSelectedPermissionIds(prev => prev.filter(id => id !== scopeId));
    }
    };

    // Add a handler for the local Apply Changes button
    const handleApplyPreAuthChanges = () => {
    // First run validation to clean up any invalid references
    syncPreAuthorizedAppsWithValidScopes();
    
    updateApplication()
        .then(() => {
        console.log("Pre-authorized applications updated successfully");
        setHasPreAuthChanges(false);
        })
        .catch(error => {
        console.error("Error updating pre-authorized applications:", error);
        
        if (error?.message?.includes("Permission Id that cannot be found")) {
            setApiError("Some pre-authorized applications reference permissions that no longer exist. Please click 'Validate Permissions' to automatically fix this issue.");
        } else {
            setApiError(`Failed to update pre-authorized applications: ${error?.message || "Unknown error"}`);
        }
        });
    };

    const preserveDisplayNames = (newApps: PreAuthorizedApplication[]): PreAuthorizedApplication[] => {
        return newApps.map(app => {
            // Check if we already have this app with a display name
            const existingApp = preAuthorizedApps.find(existing => existing.appId === app.appId);
            if (existingApp?.displayName) {
            return {
                ...app,
                displayName: existingApp.displayName
            };
            }
            
            // Otherwise use display name from cache
            return {
            ...app,
            displayName: app.appId ? appDisplayNameCache[app.appId] || app.displayName : app.displayName
            };
        });
    };

    // Add this function to your component to clean up deleted scope references
    const syncPreAuthorizedAppsWithValidScopes = () => {
        // Get all valid scope IDs
        const validScopeIds = scopes.map(scope => scope.id);
        
        // Check each pre-authorized app for invalid scopes
        let hasUpdates = false;
        
        const updatedPreAuthorizedApps = preAuthorizedApps.map(app => {
            // Filter out scope IDs that no longer exist
            const validPermissionIds = app.delegatedPermissionIds.filter(
            id => validScopeIds.includes(id)
            );
            
            // Check if any scopes were removed
            if (validPermissionIds.length !== app.delegatedPermissionIds.length) {
            hasUpdates = true;
            return {
                ...app,
                delegatedPermissionIds: validPermissionIds
            };
            }
            
            return app;
        });
        
        // Only update if changes were made
        if (hasUpdates) {
            // Update local state
            setPreAuthorizedApps(updatedPreAuthorizedApps);
            
            // Update the modifiedApp
            setModifiedApp(prev => ({
            ...prev,
            api: {
                ...(prev.api || {}),
                preAuthorizedApplications: updatedPreAuthorizedApps.map(transformPreAuthAppForGraphApi)
            }
            }));
            
            setHasPreAuthChanges(true);
            
            // Show notification to the user
            setApiError("Some pre-authorized applications referenced deleted scopes. These references have been automatically removed. Please apply changes to save.");
        }
    };   

    // --- Save All Changes ---
    const handleSaveChanges = async () => {
        setApiError(null); // Clear any previous errors

        try {
            // Prepare the final application object without calling setModifiedApp
            let finalApp = { ...modifiedApp };

            // Only validate the App ID URI if exposing an API is enabled
            if (exposeApiEnabled) {
                if (!appIdUri) {
                    setApiError("Application ID URI cannot be empty when exposing an API");
                    return;
                }
                try {
                    if (appIdUri.includes('://')) {
                        new URL(appIdUri);
                        if (appIdUri.startsWith('api://')) {
                            const path = appIdUri.substring(6);
                            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                            const domainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\/[a-z0-9\-._~%!$&'()*+,;=:@\/]+$/i;
                            if (!uuidPattern.test(path) && !domainPattern.test(path)) {
                                setApiError("App ID URI must use format api://{appId} or api://{domain}/{path} with a valid domain name and path");
                                return;
                            }
                        }
                    } else if (appIdUri.startsWith('urn:')) {
                        if (appIdUri.length <= 4) {
                            setApiError("URN format requires additional identifier after 'urn:'");
                            return;
                        }
                    } else {
                        setApiError("App ID URI must start with api://, https://, or urn:");
                        return;
                    }
                } catch (e) {
                    setApiError("Application ID URI must be a valid URI format");
                    return;
                }
                finalApp.identifierUris = [appIdUri];
            } else {
                finalApp.identifierUris = [];
            }

            // Check for duplicate scope values
            if (exposeApiEnabled && finalApp.api?.oauth2PermissionScopes) {
                const scopeValues = finalApp.api.oauth2PermissionScopes.map(s => s.value);
                const uniqueScopeValues = new Set(scopeValues);
                if (scopeValues.length !== uniqueScopeValues.size) {
                    setApiError("Duplicate scope values detected. Each scope must have a unique value.");
                    return;
                }
            }

            // Track save attempts for user guidance
            const currentAttempt = saveAttemptCount + 1;
            setSaveAttemptCount(currentAttempt);

            // Show guidance after first attempt when enabling API exposure
            if (exposeApiEnabled && hasToggleChange && currentAttempt === 1) {
                setShowSecondClickGuidance(true);
            }

            setModifiedApp(finalApp);
            await updateApplication();
            console.log("API settings updated successfully");

            // --- Reset state logic ---
            // If disabling, always reset after first click.
            // If enabling, only reset after the *second* click (when guidance is already shown).
            if (!exposeApiEnabled || (exposeApiEnabled && showSecondClickGuidance && currentAttempt > 1)) {
                setHasToggleChange(false);
                setSaveAttemptCount(0);
                setShowSecondClickGuidance(false);
            }
            // Otherwise, keep guidance state so the Complete Setup button remains visible

        } catch (error: unknown) {
            console.error("Error in ExposeApi.handleSaveChanges:", error);
            const errorMessage = error instanceof Error ? error.message :
                typeof error === 'string' ? error :
                'An unknown error occurred';
            if (errorMessage.includes("value is required but is empty")) {
                setApiError("One or more required fields are missing. Please ensure all required fields have values.");
            } else {
                setApiError(`Failed to update API settings. Please check your permissions and try again. ${errorMessage}`);
            }
        }
    };

    return (
        <>
            <Card 
            variant="outlined" 
            sx={{ 
                mb: 4,
                border: '1px solid #e8eaf6', // Light indigo border like GeneralSettings
                borderRadius: '12px !important', // Rounded corners like GeneralSettings
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', // Same shadow as GeneralSettings
            }}
            >
            <CardContent sx={{ 
                background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)', // Indigo gradient header
                borderBottom: '1px solid #c5cae9', // Indigo border separator
                mb: 0 // Remove default margin
            }}>
                {/* Enhanced title section with icon and styling to match GeneralSettings */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    backgroundColor: '#3f51b5', // Indigo background like GeneralSettings
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                }}>
                    <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
                </Box>
                <Typography 
                    variant="h6" 
                    sx={{ 
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontSize: "1.2rem", // Match GeneralSettings sizing
                    fontWeight: 600,
                    color: "#283593", // Dark indigo text like GeneralSettings
                    letterSpacing: "0.01em",
                    flexGrow: 1
                    }}
                >
                    API Exposure
                </Typography>
                <Chip 
                    size="small"
                    label="API Configuration" 
                    sx={{ 
                    backgroundColor: '#c5cae9', // Light indigo background like GeneralSettings
                    color: '#283593', // Dark indigo text
                    fontWeight: 500
                    }}
                />
                </Box>
                
                {/* <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 0 }}>
                Configure this application to expose an API that other applications can request permissions to access. Define a unique Application ID URI and the permission scopes for delegated access.
                </Typography> */}
            </CardContent>

            {/* Main content area with light indigo background */}
            <CardContent sx={{ backgroundColor: "#fafbff", p: 3 }}> {/* Light indigo background like GeneralSettings */}
                
                <Divider sx={{ my: 2 }} />

            {/* Enhanced toggle switch section with more emphasis */}
            <Box sx={{ 
                mb: 3, 
                p: 3, // Add padding around the toggle
                backgroundColor: '#ffffff', // White background to make it stand out
                borderRadius: '12px', // Rounded corners
                border: '2px solid #3f51b5', // Prominent indigo border
                boxShadow: '0 4px 16px rgba(63, 81, 181, 0.1)', // Subtle indigo shadow
                position: 'relative'
                }}>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                    width: 32, // Slightly larger icon container
                    height: 32,
                    borderRadius: '8px',
                    backgroundColor: '#3f51b5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                    }}>
                    <SecurityIcon sx={{ color: 'white', fontSize: '1rem' }} />
                    </Box>
                    
                    <FormControlLabel
                    control={
                        <Switch
                        checked={exposeApiEnabled}
                        onChange={(e) => {
                            setExposeApiEnabled(e.target.checked);
                            setHasToggleChange(true); // Add this line
                            
                            // If turning off, clear the URI
                            if (!e.target.checked) {
                            setAppIdUri("");
                            setModifiedApp(prev => ({
                                ...prev,
                                identifierUris: [] // Empty array to remove all URIs
                            }));
                            } 
                            // If turning on and no URI set, suggest the default
                            else if (e.target.checked && !appIdUri) {
                            const defaultUri = `api://${modifiedApp.appId}`;
                            setAppIdUri(defaultUri);
                            setModifiedApp(prev => ({
                                ...prev,
                                identifierUris: [defaultUri]
                            }));
                            }
                        }}
                        color="primary"
                        size="medium"
                        />
                    }
                    label={
                        <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        fontSize: '1.2rem',
                        color: '#283593'
                        }}>
                        Expose an API
                        </Typography>
                    }
                    sx={{ flexGrow: 1 }}
                    />
                    
                    {/* Status indicator */}
                    <Chip 
                    label={exposeApiEnabled ? "Enabled" : "Disabled"}
                    size="small"
                    color={exposeApiEnabled ? "success" : "default"}
                    sx={{ 
                        fontWeight: 500,
                        height: 24
                    }}
                    />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mb: 2 }}>
                    {exposeApiEnabled 
                    ? "This application is configured to expose an API that other applications can access"
                    : "Enable this option if you want other applications to access this application's API"
                    }
                </Typography>

                {/* Application ID URI section - now inside the toggle box */}
                {exposeApiEnabled && (
                    <Box sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "#fafbff", // Light indigo background
                    borderRadius: '8px', // Rounded corners
                    border: "1px solid #e8eaf6" // Light indigo border
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        backgroundColor: '#3f51b5', // Indigo background
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                        }}>
                        <DomainVerificationIcon sx={{ color: 'white', fontSize: '0.75rem' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#283593' }}>
                        Step 1: Application ID URI
                        </Typography>
                        <Tooltip title="This globally unique URI identifies your API. Client applications use it to request tokens for your API (it becomes the 'aud' claim). It's also used when defining permissions.">
                        <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TextField
                        fullWidth
                        label="Application ID URI"
                        value={appIdUri}
                        onChange={handleAppIdUriChange}
                        variant="outlined"
                        size="small"
                        helperText={`Default: api://${app.appId}`}
                        />
                        <Tooltip title="Copy URI">
                        <IconButton onClick={() => handleCopyToClipboard(appIdUri)} sx={{ ml: 1 }}>
                            <ContentCopyIcon />
                        </IconButton>
                        </Tooltip>
                    </Box>
                    
                    <Alert severity="info" sx={{ fontSize: '0.875rem' }} icon={<InfoOutlinedIcon fontSize="inherit" />}>
                        Changing the App ID URI can break existing client applications that rely on the old URI to acquire tokens. Ensure clients are updated if you change this value.
                    </Alert>
                    </Box>
                )}
                </Box>

                {/* Apply Changes button section - moved outside but still visible when enabled */}
                {(exposeApiEnabled || hasToggleChange || (!exposeApiEnabled && (modifiedApp.identifierUris && modifiedApp.identifierUris.length > 0))) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, flexDirection: 'column' }}>
                    {/* Enhanced guidance when enabling API exposure */}
                    {exposeApiEnabled && hasToggleChange && showSecondClickGuidance && (
                    <Alert 
                        severity="info" 
                        sx={{ mb: 2, width: '100%' }}
                        icon={<InfoOutlinedIcon />}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        Please click "Apply Changes" once more to complete the API exposure setup
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                        Due to Azure's state synchronization, enabling API exposure requires two save operations. 
                        This ensures all configuration is properly committed to Microsoft Graph.
                        </Typography>
                    </Alert>
                    )}
                    
                    {/* Standard guidance when disabling */}
                    {!exposeApiEnabled && hasToggleChange && !showSecondClickGuidance && (
                    <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                        Click "Apply Changes" to disable API exposure and remove the Application ID URI.
                    </Alert>
                    )}
                    
                    {apiError && (
                    <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                        {apiError}
                    </Alert>
                    )}
                    
                    <Button
                    variant="contained"
                    color="success"
                    size="medium"
                    startIcon={<SaveIcon />}
                    onClick={() => {
                        handleSaveChanges();
                    }}
                    sx={{ 
                        borderRadius: '8px',
                        alignSelf: 'flex-end',
                        textTransform: 'none',
                        fontWeight: 500,
                        // Add visual emphasis when second click is needed
                        ...(showSecondClickGuidance && {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' }
                        }
                        })
                    }}
                    >
                    {showSecondClickGuidance ? 'Complete Setup' : 'Apply Changes'}
                    {showSecondClickGuidance && (
                        <Chip 
                        label="2/2" 
                        size="small" 
                        sx={{ 
                            ml: 1, 
                            height: 20, 
                            backgroundColor: 'white', 
                            color: 'success.main',
                            fontWeight: 600
                        }} 
                        />
                    )}
                    </Button>
                </Box>
                )}

                <Divider sx={{ mb: 3 }} />

                {exposeApiEnabled ? (
                <>

                    {/* --- Scopes Section --- */}
                    <Box sx={{ 
                    p: 3, 
                    backgroundColor: "#fafbff", // Light indigo background
                    borderRadius: '12px',
                    border: "1px solid #e8eaf6", // Indigo border
                    mb: 3
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            backgroundColor: '#3f51b5', // Indigo background
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5
                        }}>
                            <VerifiedUserIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#283593' }}>
                            Scopes defined by this API
                        </Typography>
                        <Tooltip title="Delegated permissions (scopes) allow client applications to act on behalf of a signed-in user, constrained by the permissions granted.">
                            <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                        </Box>
                        <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddScopeDialog}
                        sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: '#3f51b5',
                            color: '#3f51b5',
                            '&:hover': {
                            borderColor: '#283593',
                            backgroundColor: '#f3e5f5'
                            }
                        }}
                        size="small"
                        >
                        Add a scope
                        </Button>                            
                    </Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Add scopes to grant access to specific resources or operations within your API.
                        </Typography>
                        {scopes.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 3, fontStyle: 'italic' }}>
                                No scopes defined yet. Click "Add a scope" to get started.
                            </Typography>
                        ) : (
                            <List dense sx={{ 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: 1, 
                                mt: 2, 
                                backgroundColor: '#ffffff'
                            }}>
                                {scopes.map((scope, index) => (
                                    <ListItem
                                        key={scope.id}
                                        secondaryAction={
                                        <>
                                        <Tooltip 
                                        title={
                                            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                                            {scope.isEnabled ? 
                                                "Disable Scope\n\nRemember to click \"Apply Changes\" to save this change." : 
                                                "Enable Scope\n\nRemember to click \"Apply Changes\" to save this change."}
                                            </Typography>
                                        }
                                        arrow
                                        >
                                        <Switch
                                            checked={scope.isEnabled}
                                            onChange={(e) => handleToggleScopeEnabled(index, e.target.checked)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        </Tooltip>
                                            <Tooltip title="Edit Scope">
                                            <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditScopeDialog(index)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            </Tooltip>
                                            <Tooltip title={scope.isEnabled ? "Must disable before deletion" : "Delete Scope"}>
                                            <span> {/* Wrap in span to allow tooltip on disabled button */}
                                                <IconButton 
                                                edge="end" 
                                                aria-label="delete" 
                                                onClick={() => handleOpenDeleteConfirm(index)} 
                                                sx={{ ml: 0.5 }}
                                                disabled={scope.isEnabled} // Disable delete button if scope is enabled
                                                >
                                                <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                            </Tooltip>
                                        </>
                                        }
                                        divider={index < scopes.length - 1}
                                        sx={{ py: 1.5, '& .MuiListItemSecondaryAction-root': { right: 12 } }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500, mr: 1 }}>
                                                    {scope.adminConsentDisplayName}
                                                </Typography>
                                                <Chip
                                                    label={scope.value}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mr: 1, height: 20, fontSize: '0.75rem' }}
                                                />
                                                {scope.type === 'Admin' && 
                                                    <Chip 
                                                        label="Admin consent" 
                                                        size="small" 
                                                        color="warning" 
                                                        icon={<AdminPanelSettingsIcon sx={{ fontSize: '0.7rem !important' }} />}
                                                        sx={{ height: 20, fontSize: '0.65rem', mr: 1 }} 
                                                    />
                                                }
                                                {!scope.isEnabled && 
                                                    <Chip 
                                                        label="Disabled" 
                                                        size="small" 
                                                        color="error" 
                                                        sx={{ height: 20, fontSize: '0.75rem' }} 
                                                    />
                                                }
                                            </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        Admin consent: {scope.adminConsentDescription || <i>No description</i>}
                                                    </Typography>
                                                    <br />
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        User consent: {scope.userConsentDisplayName || <i>Defaults to admin display name</i>} - {scope.userConsentDescription || <i>No description</i>}
                                                    </Typography>
                                                </>
                                            }
                                            primaryTypographyProps={{ component: 'div' }}
                                            secondaryTypographyProps={{ component: 'div' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                    </>
                ) : (
                <Box sx={{ 
                    p: 4, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '12px', 
                    textAlign: 'center',
                    border: '2px dashed #dee2e6'
                }}>
                    <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: '#e9ecef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                    }}>
                    <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    API exposure is currently disabled for this application
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    Toggle the switch above to configure this application to expose an API that other applications can access
                    </Typography>
                </Box>
                )}

                    <Divider sx={{ my: 3 }} />

                    {/* --- Pre-Authorized Applications Section --- */}
                    {exposeApiEnabled && (
                    <Box sx={{ 
                    p: 3, 
                    backgroundColor: "#fafbff", // Light indigo background
                    borderRadius: '12px',
                    border: "1px solid #e8eaf6", // Indigo border
                    mb: 3
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            backgroundColor: '#3f51b5', // Indigo background
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.5
                        }}>
                            <MeetingRoomIcon sx={{ color: 'white', fontSize: '0.85rem' }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#283593' }}>
                            Pre-authorized Client Applications
                        </Typography>
                        <Tooltip title="Pre-authorize trusted client applications to access this API without requiring user or admin consent.">
                            <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                        </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Check and remove references to deleted permissions">
                                <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<VerifiedUserIcon />}
                                onClick={syncPreAuthorizedAppsWithValidScopes}
                                sx={{ borderRadius: 2 }}
                                >
                                Validate Permissions
                                </Button>
                            </Tooltip>
                            
                            {hasPreAuthChanges && (
                                <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<SaveIcon />}
                                onClick={handleApplyPreAuthChanges}
                                sx={{ borderRadius: 2 }}
                                >
                                Apply Changes
                                </Button>
                            )}
                            
                            <Button
                                variant="outlined"
                                startIcon={<MeetingRoomIcon />}
                                onClick={handleOpenPreAuthorizedAppDialog} // Use the new handler instead
                                sx={{ borderRadius: 2 }}
                                size="small"
                            >
                                Add Client Application
                            </Button>
                            </Box>
                        </Box>
                        
                        {/* Optional notification when changes are pending */}
                        {hasPreAuthChanges && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            You have made changes to pre-authorized applications. Click "Apply Changes" to save.
                        </Alert>
                        )}

                        <Typography variant="body2" color="text.secondary" paragraph>
                            These client applications are pre-authorized to access your API without showing a consent prompt.
                        </Typography>
                        
                        {preAuthorizedApps.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 3, fontStyle: 'italic' }}>
                            No pre-authorized client applications. Add one to streamline access to your API.
                        </Typography>
                        ) : (
                        <List dense sx={{ 
                            border: '1px solid', 
                            borderColor: 'divider', 
                            borderRadius: 1, 
                            mt: 2, 
                            backgroundColor: '#ffffff'
                        }}>
                            {preAuthorizedApps.map((app, index) => (
                            <ListItem
                            key={app.appId}
                            secondaryAction={
                                <>
                                <Tooltip title="Edit permissions">
                                    <IconButton 
                                    edge="end" 
                                    aria-label="edit" 
                                    onClick={() => { if (app.appId) { handleEditPreAuthorizedApp(index); } }}
                                    sx={{ mr: 1 }}
                                    disabled={!app.appId}
                                    >
                                    <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove pre-authorization">
                                    <IconButton 
                                    edge="end" 
                                    aria-label="delete" 
                                    onClick={() => { if (app.appId) { handleRemovePreAuthorizedApp(app.appId); } }}
                                    sx={{ ml: 0.5 }}
                                    disabled={!app.appId}
                                    >
                                    <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                </>
                            }
                            divider={index < preAuthorizedApps.length - 1}
                            sx={{ py: 1.5, '& .MuiListItemSecondaryAction-root': { right: 12 } }}
                            >
                                <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <DomainVerificationIcon sx={{ mr: 1, color: 'success.main', fontSize: '1.1rem' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 500, mr: 1 }}>
                                    {app.displayName || (app.appId && appDisplayNameCache[app.appId]) || "Unknown Client Application"}
                                    </Typography>
                                    <Tooltip title="Application ID">
                                        <Chip
                                        label={app.appId}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mr: 1, height: 20, fontSize: '0.75rem' }}
                                        />
                                    </Tooltip>
                                    </Box>
                                }
                                secondary={
                                    <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        Granted Access To:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {app.delegatedPermissionIds.map(scopeId => {
                                        // Find the scope name/value based on the ID
                                        const scope = scopes.find(s => s.id === scopeId);
                                        return (
                                            <Chip
                                            key={scopeId}
                                            label={scope?.value || scopeId}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: '0.75rem' }}
                                            />
                                        );
                                        })}
                                    </Box>
                                    </Box>
                                }
                                primaryTypographyProps={{ component: 'div' }}
                                secondaryTypographyProps={{ component: 'div' }}
                                />
                            </ListItem>
                            ))}
                        </List>
                        )}
                    </Box>
                    )}

                    {/* --- Pre-authorized Application Dialog --- */}
                    <Dialog 
                    open={preAuthorizedAppDialogOpen} 
                    onClose={resetPreAuthorizedAppDialog}
                    maxWidth="md"
                    fullWidth
                    >
                    <DialogTitle>
                    {isEditingPreAuthorizedApp ? 'Edit Pre-authorized Client Application' : 'Pre-authorize Client Application'}
                    </DialogTitle>
                    
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                        Pre-authorized client applications can access this API without showing consent prompts to users or requiring admin consent.
                        </Typography>
                        
                        <Alert severity="info" sx={{ mb: 2 }}>
                        This is recommended for first-party apps within your organization to create a seamless user experience.
                        </Alert>
                        
                        {preAuthErrorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {preAuthErrorMessage}
                        </Alert>
                        )}
                        
                        <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Client Application
                        </Typography>
                        
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} md={8}>
                                <TextField
                                fullWidth
                                label="Client Application ID (App ID)"
                                value={preAuthorizedAppId}
                                onChange={(e) => setPreAuthorizedAppId(e.target.value)}
                                size="small"
                                helperText="Enter the Application (client) ID of the app you want to pre-authorize"
                                disabled={isEditingPreAuthorizedApp} // Disable when editing
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {!isEditingPreAuthorizedApp && (
                                <Button
                                    variant="outlined"
                                    onClick={() => validateAppId(preAuthorizedAppId)}
                                    disabled={isValidatingAppId}
                                    sx={{ height: 40 }}
                                >
                                    {isValidatingAppId ? "Validating..." : "Validate App ID"}
                                </Button>
                                )}
                            </Grid>
                        </Grid>
                        
                        {validatedAppInfo && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Application validated: {validatedAppInfo.displayName}
                            </Typography>
                            <Typography variant="caption">
                                App ID: {validatedAppInfo.appId}
                            </Typography>
                            </Alert>
                        )}
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Select permissions to grant to this client application:
                        </Typography>
                        
                        {scopes.length === 0 ? (
                        <Alert severity="warning">
                            You must define at least one API permission scope before pre-authorizing applications.
                        </Alert>
                        ) : (
                        <Box sx={{ 
                            maxHeight: 300, 
                            overflow: 'auto', 
                            border: '1px solid', 
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1
                        }}>
                            <List dense>
                            {scopes.map((scope) => (
                                <ListItem key={scope.id} disablePadding>
                                <ListItemText
                                    primary={
                                    <FormControlLabel
                                        control={
                                        <Checkbox
                                            checked={selectedPermissionIds.includes(scope.id)}
                                            onChange={(e) => handlePermissionSelectionChange(e, scope.id)}
                                            disabled={!scope.isEnabled}
                                        />
                                        }
                                        label={
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {scope.adminConsentDisplayName}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                            {scope.value} - {scope.adminConsentDescription}
                                            </Typography>
                                            {!scope.isEnabled && (
                                            <Typography variant="caption" color="error">
                                                This scope is currently disabled
                                            </Typography>
                                            )}
                                        </Box>
                                        }
                                    />
                                    }
                                />
                                </ListItem>
                            ))}
                            </List>
                        </Box>
                        )}
                    </DialogContent>
                        <DialogActions>
                        <Button onClick={resetPreAuthorizedAppDialog}>Cancel</Button>
                        <Button 
                            onClick={handleAddOrUpdatePreAuthorizedApp}
                            variant="contained"
                            disabled={!validatedAppInfo || selectedPermissionIds.length === 0}
                        >
                            {isEditingPreAuthorizedApp ? 'Update Permissions' : 'Add & Return to List'}
                        </Button>
                        </DialogActions>
                    </Dialog>

                    {/* --- About Exposing an API Accordion --- */}
                    <Accordion
                    sx={{
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                        opacity: 0.85,
                        mt: 3
                    }}
                    >
                    <AccordionSummary 
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />}
                        sx={{
                        background: '#f8f9fa',
                        borderBottom: '1px solid #e9ecef',
                        minHeight: '48px',
                        '&:hover': { backgroundColor: '#f1f3f4' }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} />
                        <Typography 
                            variant="subtitle1"
                            sx={{ 
                            fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            letterSpacing: "0.005em"
                            }}
                        >
                            About Exposing an API
                        </Typography>
                        <Chip 
                            size="small"
                            label="Info" 
                            variant="outlined" 
                            sx={{ 
                            ml: 2,
                            borderColor: '#dee2e6',
                            color: '#6c757d',
                            fontSize: '0.65rem', 
                            height: '20px', 
                            fontWeight: 400
                            }}
                        />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Exposing an API allows your application to share its functionality with other applications in a secure and controlled manner through Microsoft Entra ID.
                                </Typography>
                                
                                {/* Visual explanation */}

                                {/* Usage guidance section - enhanced styling */}
                                <Box sx={{ 
                                p: 2, 
                                mb: 2,
                                backgroundColor: "#ffffff", // White background for contrast
                                borderRadius: '8px', // Rounded corners
                                border: "1px solid #e8eaf6" // Light indigo border
                                }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#283593', fontWeight: 600 }}>
                                    When to Expose an API
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>For API/Web Service Applications:</strong> If this application acts as an API or backend service that 
                                    other applications will call, you should expose an API. This enables other applications to request access 
                                    to your functionality through proper OAuth 2.0 authorization flows.
                                </Typography>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    <strong>For Client Applications:</strong> Most client applications (native, SPA, or confidential clients) don't need 
                                    to expose an API unless they implement app-to-app communication patterns or service endpoints. 
                                    If this application only consumes APIs but doesn't provide services to other applications, 
                                    you likely don't need to configure this section.
                                </Typography>
                                </Box>

                                <Box sx={{ 
                                    p: 2, 
                                    backgroundColor: '#ffffff', 
                                    border: '1px solid #e0e0ff', 
                                    borderRadius: 1,
                                    mb: 2
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                                        Key Concepts:
                                    </Typography>
                                    <Typography variant="body2">
                                        • <strong>Application ID URI</strong>: A globally unique identifier for your API (e.g., api://1234-5678-9abc-defg/)<br />
                                        • <strong>Permission Scopes</strong>: Granular access controls that client applications can request<br />
                                        • <strong>Delegated Permissions</strong>: Access your API on behalf of a signed-in user<br />
                                        • <strong>Application Permissions</strong>: Access your API without a user (service-to-service)
                                    </Typography>
                                </Box>
                                
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: '#fff8e1',
                                    border: '1px solid #ffe082',
                                    borderRadius: 1
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                                        Best Practices:
                                    </Typography>
                                    <Typography variant="body2">
                                        • Use meaningful names and descriptions for your permission scopes<br />
                                        • Define the minimum set of permissions needed for different client scenarios<br />
                                        • Consider who will need admin consent versus user consent for each permission<br />
                                        • For custom domains in Application ID URIs, verify domain ownership in Entra ID
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                </CardContent>
            </Card>

            {/* --- Add/Edit Scope Dialog --- */}
            <Dialog open={scopeDialogOpen} onClose={handleScopeDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingScopeIndex !== null ? 'Edit Scope' : 'Add Scope'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                required
                                margin="dense"
                                id="scopeValue"
                                label="Scope name (e.g., Files.Read)"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={currentScope.value || ''}
                                onChange={(e) => handleScopeChange('value', e.target.value)}
                                error={!!scopeErrors.value}
                                helperText={scopeErrors.value || "The permission value clients will request. No spaces allowed."}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 500 }}>Permission Type</Typography>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel id="permission-type-label">Who can consent</InputLabel>
                                <Select
                                labelId="permission-type-label"
                                id="permission-type"
                                value={currentScope.type || 'User'}
                                onChange={(e) => handleScopeChange('type', e.target.value)}
                                label="Who can consent"
                                >
                                <MenuItem value="User">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                                    <Box>
                                        <Typography variant="body2">User and admin consent</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                        Users can consent to this permission, suitable for less sensitive operations
                                        </Typography>
                                    </Box>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="Admin">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'warning.main' }} />
                                    <Box>
                                        <Typography variant="body2">Admin consent only</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                        Only administrators can consent to this permission, suitable for sensitive operations
                                        </Typography>
                                    </Box>
                                    </Box>
                                </MenuItem>
                                </Select>
                                <FormHelperText>
                                {currentScope.type === 'Admin' ? 
                                    'This permission requires administrator approval and cannot be consented to by users directly.' : 
                                    'This permission can be consented to by users without administrator approval.'}
                                </FormHelperText>
                            </FormControl>
                            </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 500 }}>Admin Consent Settings</Typography>
                            <TextField
                                required
                                margin="dense"
                                id="adminConsentDisplayName"
                                label="Admin consent display name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={currentScope.adminConsentDisplayName || ''}
                                onChange={(e) => handleScopeChange('adminConsentDisplayName', e.target.value)}
                                error={!!scopeErrors.adminConsentDisplayName}
                                helperText={scopeErrors.adminConsentDisplayName || "Short name shown on admin consent screen."}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required // Add required attribute
                                margin="dense"
                                id="adminConsentDescription"
                                label="Admin consent description"
                                type="text"
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                value={currentScope.adminConsentDescription || ''}
                                onChange={(e) => handleScopeChange('adminConsentDescription', e.target.value)}
                                error={!!scopeErrors.adminConsentDescription} // Add error state
                                helperText={scopeErrors.adminConsentDescription || "Detailed description shown on admin consent screen. Required by Microsoft Graph API."} // Update help text
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>User Consent Settings (Optional)</Typography>
                            <TextField
                                margin="dense"
                                id="userConsentDisplayName"
                                label="User consent display name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={currentScope.userConsentDisplayName || ''}
                                onChange={(e) => handleScopeChange('userConsentDisplayName', e.target.value)}
                                helperText="Short name shown on user consent screen (if different from admin)."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                id="userConsentDescription"
                                label="User consent description"
                                type="text"
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                value={currentScope.userConsentDescription || ''}
                                onChange={(e) => handleScopeChange('userConsentDescription', e.target.value)}
                                helperText="Detailed description shown on user consent screen (if different from admin)."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={currentScope.isEnabled}
                                        onChange={(e) => handleScopeChange('isEnabled', e.target.checked)}
                                        name="isEnabled"
                                        color="primary"
                                    />
                                }
                                label="Enabled"
                            />
                            <Typography variant="caption" display="block" color="textSecondary">
                                If disabled, clients cannot request this scope.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                margin="dense"
                                id="scopeId"
                                label="Scope ID (auto-generated)"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={(currentScope as PermissionScope).id || ''}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleScopeDialogClose}>Cancel</Button>
                    <Button onClick={handleSaveScope} variant="contained">
                        {editingScopeIndex !== null ? 'Save Changes' : 'Add Scope'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Delete Confirmation Dialog --- */}
            <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    {scopeToDeleteIndex !== null && (
                    <>
                        {scopes[scopeToDeleteIndex]?.isEnabled ? (
                        // Case 1: Scope is enabled - can't delete
                        <>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                            This scope is currently enabled and cannot be deleted.
                            </Alert>
                            <Typography>
                            Following Microsoft Entra ID security practices, permission scopes must be disabled first before deletion. 
                            This prevents accidentally breaking client applications that depend on these permissions.
                            </Typography>
                            <Button 
                            onClick={() => {
                                handleToggleScopeEnabled(scopeToDeleteIndex, false);
                                handleCloseDeleteConfirm();
                            }}
                            variant="contained" 
                            color="primary" 
                            sx={{ mt: 2 }}
                            >
                            Disable scope now
                            </Button>
                        </>
                        ) : (
                        // Case 2: Scope is disabled - enhance warning about references
                        <>
                            <Alert severity="warning" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Warning: Deleting a scope may break existing client applications
                                </Typography>
                                <Typography variant="body2">
                                If this scope is currently referenced by pre-authorized applications, deleting it will create orphaned references.
                                </Typography>
                            </Box>
                            </Alert>
                            
                            <Typography paragraph>
                            Are you sure you want to delete the scope{' '}
                            <strong>{scopes[scopeToDeleteIndex]?.adminConsentDisplayName}</strong> ({scopes[scopeToDeleteIndex]?.value})?
                            </Typography>
                            
                            <Typography paragraph sx={{ color: 'text.secondary' }}>
                            This action cannot be undone. After deletion, pre-authorized applications that referenced this scope will have orphaned permissions.
                            You will need to run "Validate Permissions" to clean up these references.
                            </Typography>
                            
                            <Box sx={{ 
                            p: 1.5, 
                            bgcolor: '#fff8e1', 
                            borderRadius: 1, 
                            border: '1px solid #ffe57f',
                            mb: 2
                            }}>
                            <Typography variant="body2">
                                <strong>Recommended steps after deletion:</strong>
                            </Typography>
                            <Typography variant="body2" component="ol" sx={{ pl: 2, mt: 1, mb: 0 }}>
                                <li>Click "Validate Permissions" in the Pre-authorized Applications section</li>
                                <li>Apply changes to remove orphaned scope references</li>
                                <li>Inform client application owners about this change</li>
                            </Typography>
                            </Box>
                            
                            <DialogActions sx={{ pl: 0, pr: 0 }}>
                            <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
                            <Button 
                                onClick={handleDeleteScopeConfirm} 
                                color="error" 
                                variant="contained"
                                startIcon={<DeleteIcon />}
                            >
                                Delete Scope
                            </Button>
                            </DialogActions>
                        </>
                        )}
                    </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ExposeApi;